import { useEffect, useRef, useState } from 'react';

import { Avatar } from '@/components/Avatar';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { ApiRequestError } from '@/lib/api/client';
import { VENDOR_KIND_LABEL, VENDOR_KINDS, useSession } from '@/lib/auth/session';
import {
  useDeleteMyPhoto,
  useMyProfile,
  useUpdateProfile,
  useUploadMyPhoto,
} from '@/lib/profile';

/**
 * 마이페이지 — 본인 정보와 프로필 사진 (사용자 결정 2026-09-30).
 *
 * <p><b>고칠 수 있는 것은 사진과 보직 둘</b>이다. 이름·이메일은 본사가 초대할 때 적은 것이 정본이고
 * (이메일은 로그인 계정이다) 업체 정보는 마스터의 것이다 — 담당자 한 사람이 회사 정보를 고치는 문을
 * 열지 않는다. 고칠 수 없는 칸은 <b>보여 주기는 한다</b>: 자기 정보가 무엇으로 적혀 있는지 모르면
 * 틀린 것을 본사에 알릴 수도 없다.
 *
 * <p>직원은 사진이 <b>인사자료의 증명사진</b>이다. 그래서 여기서 바꿀 수 없는데, 버튼을 감추지는
 * 않는다 — 못 하는 이유는 눌렀을 때 서버가 문장으로 답한다(프로젝트 규율).
 */
export function MyPage() {
  const profile = useMyProfile();
  const session = useSession();
  const update = useUpdateProfile();
  const upload = useUploadMyPhoto();
  const remove = useDeleteMyPhoto();

  const picker = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState('');

  const loaded = profile.data;

  // 서버 값이 오면 칸을 채운다. 사람이 적고 있는 중에는 덮지 않는다(저장 뒤 setQueryData 로만 바뀐다).
  useEffect(() => {
    if (loaded !== undefined) setPosition(loaded.positionTitle ?? '');
  }, [loaded]);

  const kinds = VENDOR_KINDS.filter((kind) => (session.data?.vendorKinds ?? []).includes(kind))
    .map((kind) => VENDOR_KIND_LABEL[kind])
    .join('·');

  /*
    서버가 거절한 이유. 사람이 <b>누른 것</b>에 대한 답이라 4xx 는 봉투의 문장을 그대로 보여 준다 —
    「직원 증명사진은 인사자료에서 바꿉니다」가 버튼을 감추지 않는 대신 사람이 받는 답이다.
    5xx 는 감춘다(내부 사정을 외부 사용자에게 알려 주지 않는다 — `QueryState` 머리말과 같은 선).
  */
  const failure = [upload.error, remove.error, update.error]
    .find((error): error is Error => error != null);
  const failureText = failure === undefined
    ? null
    : failure instanceof ApiRequestError && failure.status < 500
      ? failure.message
      : '지금은 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';

  return (
    <>
      <PageHeader title="마이페이지" back={false} />

      <QueryState
        pending={profile.isPending}
        error={profile.isError}
        empty={loaded === undefined}
        emptyText="내 정보를 불러오지 못했습니다."
      >
        <div className="flex flex-col gap-3">
          <section className="card flex items-center gap-4">
            <Avatar size={72} name={loaded?.name} />

            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="min-w-0">
                <div className="truncate text-ui-lg font-semibold">{loaded?.name}</div>
                <div className="truncate text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
                  {loaded?.partyName}
                  {kinds !== '' && ` · ${kinds}`}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {/* 파일 칸은 숨기고 버튼이 대신 연다 — 브라우저 기본 모양이 카드와 어울리지 않는다. */}
                <input
                  ref={picker}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    // 같은 파일을 다시 골라도 change 가 나게 비운다.
                    event.target.value = '';
                    if (file !== undefined) upload.mutate(file);
                  }}
                />
                <button
                  type="button"
                  className="btn-secondary px-3 py-1.5 text-ui-xs"
                  disabled={upload.isPending}
                  onClick={() => picker.current?.click()}
                >
                  {upload.isPending ? '올리는 중입니다' : '사진 올리기'}
                </button>
                {/*
                  사진이 없을 때 「내리기」를 두지 않는 것은 조건부 숨김이 아니다 — 내릴 대상이 없다.
                  「직원은 못 내린다」 같은 판정으로는 감추지 않는다(눌렀을 때 서버가 답한다).
                */}
                {loaded?.hasPhoto === true && (
                  <button
                    type="button"
                    className="btn-secondary px-3 py-1.5 text-ui-xs"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate()}
                  >
                    사진 내리기
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="card flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-ui-xs" style={{ color: 'var(--text-secondary)' }}>보직</span>
              <input
                className="input-field"
                value={position}
                maxLength={60}
                placeholder="현장소장"
                onChange={(event) => setPosition(event.target.value)}
              />
            </label>

            <button
              type="button"
              className="btn-primary self-start px-4 py-1.5 text-ui-sm"
              disabled={update.isPending}
              onClick={() => update.mutate(position)}
            >
              {update.isPending ? '저장하는 중입니다' : '저장'}
            </button>
          </section>

          {/* 고칠 수 없는 칸. 값이 없는 줄은 세우지 않는다 — 직원에게는 전화 칸이 없다. */}
          <section className="card">
            <dl className="flex flex-col gap-2 text-ui-sm">
              <div className="flex gap-3">
                <dt className="w-20 shrink-0" style={{ color: 'var(--text-muted)' }}>이메일</dt>
                <dd className="min-w-0 truncate">{loaded?.email}</dd>
              </div>
              {loaded?.phone != null && (
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0" style={{ color: 'var(--text-muted)' }}>전화</dt>
                  <dd className="min-w-0 truncate">{loaded.phone}</dd>
                </div>
              )}
              <div className="flex gap-3">
                <dt className="w-20 shrink-0" style={{ color: 'var(--text-muted)' }}>소속</dt>
                <dd className="min-w-0 truncate">{loaded?.partyName}</dd>
              </div>
            </dl>
          </section>

          {failureText !== null && (
            <p className="text-ui-sm" style={{ color: 'var(--danger)' }} role="alert">
              {failureText}
            </p>
          )}
        </div>
      </QueryState>
    </>
  );
}
